import * as React from 'react';
import styled from 'styled-components';
import { ImageProps as NextImageProps } from 'next/image';
import { Document as ContentfulDocument } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import PrizePool from '@mybet/core/blocks/PrizePool';
import RaffleCampaignBanner from '@mybet/core/blocks/RaffleCampaignBanner';
import ErrorBoundary from '@mybet/core/components/ErrorBoundary';
import NoSsr from '@mybet/core/components/NoSsr';
import {
  Section,
  Spacing,
  Title,
  CampaignTypography,
  CampaignButton,
  List,
  ExpansionPanel,
  Header,
  PayPalDisclaimer,
  Promotion,
  CasinoGameSection,
} from '@mybet/components';

enum PageBlocks {
  section = 'section',
  spacing = 'spacing',
  title = 'title',
  typography = 'typography',
  list = 'list',
  paypalDisclaimer = 'paypalDisclaimer',
  promotion = 'promotion',
  cta = 'cta',
  header = 'header',
  expandableList = 'expandableList',
  prizeSection = 'prizeSection',
  casinoGameSection = 'casinoGameSection',
  banner = 'banner',
}

export type PageBlock = keyof typeof PageBlocks;
export type BlockConfig = {
  block: PageBlock;
  properties: Record<string, unknown>;
  blockIndex: number;
  sectionIndex: number;
  localeKeys?: Record<string, string | Record<string, string>[]> | null;
  richTextKeys?: Record<string, string> | null;
};
export type SectionConfig = {
  block: PageBlock;
  properties: { srcDesktop?: string; srcMobile?: string };
  sectionIndex: number;
  contentBlocks: BlockConfig[];
};
export type PageConfig = SectionConfig[];
export type CampaignPageProps = {
  pageConfig: PageConfig;
  onButtonClick: (
    type: 'link' | 'action',
    value: 'login' | 'register' | string
  ) => void;
  onCasinoGameClick(slug: string): void;
  ImageProps?: Omit<
    NextImageProps,
    'src' | 'width' | 'height' | 'layout' | 'objectFit'
  >;
  onBannerClick(): void;
};

const BLOCKS_MAP = {
  section: Section,
  spacing: Spacing,
  title: Title,
  typography: CampaignTypography,
  list: List,
  paypalDisclaimer: PayPalDisclaimer,
  promotion: Promotion,
  cta: CampaignButton, // NOTE: should be replaced with Button once updated properly. But then we need to wrap link if applicable before render
  header: Header,
  expandableList: ExpansionPanel,
  casinoGameSection: CasinoGameSection,
  prizeSection: PrizePool,
  banner: RaffleCampaignBanner,
};

const CampaginPageRoot = styled.div(({ theme }) => ({
  fontFamily: '"Roboto", sans-serif',
  width: '100%',
  height: '100%',
  color: theme.white,
  fontWeight: 400,
  '& *': {
    zIndex: 5,
    boxSizing: 'border-box',
  },
}));

const CampaignPage: React.FC<CampaignPageProps> = (props) => {
  const {
    pageConfig,
    onButtonClick,
    onBannerClick,
    onCasinoGameClick,
    ImageProps,
  } = props;
  return (
    <CampaginPageRoot>
      {pageConfig.map(
        (
          { block, properties: { srcDesktop, srcMobile }, contentBlocks },
          i: number
        ) => {
          const Block = BLOCKS_MAP[block] as any;
          if (!Block) return null;
          return (
            <ErrorBoundary key={`${block}${i}`}>
              <Block
                key={`${block}${i}`}
                srcDesktop={srcDesktop}
                srcMobile={srcMobile}
              >
                {contentBlocks.map(({ block, properties }, i: number) => {
                  const ContentBlock = BLOCKS_MAP[block] as any;
                  if (!ContentBlock) return null;

                  // add handleClick prop to button from page
                  // as this needs to come from invoking application
                  // to properly handle actions and links
                  let props = properties;
                  if (block === 'cta') {
                    props = {
                      ...properties,
                      href: properties.target,
                      handleClick: onButtonClick,
                    };
                  }

                  if (block === 'casinoGameSection') {
                    props = {
                      ...properties,
                      onClick: onCasinoGameClick,
                    };
                  }

                  if (block === 'prizeSection') {
                    props = {
                      pools: properties.items,
                    };
                  }

                  if (block === 'banner') {
                    props = {
                      ImageProps,
                      onClick: onBannerClick,
                    };
                    return (
                      <NoSsr>
                        <ContentBlock key={`${block}${i}`} {...props} />
                      </NoSsr>
                    );
                  }

                  if (block === 'expandableList') {
                    const { content, ...rest } = props;
                    return (
                      <ContentBlock key={`${block}${i}`} {...rest}>
                        {documentToReactComponents(
                          content as ContentfulDocument
                        )}
                      </ContentBlock>
                    );
                  }

                  return <ContentBlock key={`${block}${i}`} {...props} />;
                })}
              </Block>
            </ErrorBoundary>
          );
        }
      )}
    </CampaginPageRoot>
  );
};

export default CampaignPage;
